import React, { useState, useEffect, useMemo, FC } from 'react';
import { useRouter } from 'next/router';
import { useT } from '@transifex/react';
import { useIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Alert, Typography } from '@raisin/components-lib';
import { DEFAULT_LOCALE, getDistributor, getLocale, isCustomerOffBoarding } from '@raisin/cp-utils';
import { useAuthContext, useHeaderDataContext } from '@raisin/cp-layout';
import { parseDepositOfferData } from '@raisin/cp-legacy-ui-lib/src/components/OffersTable/utils';
import {
  buildAsyncRegistrationLink,
  buildOpeningOrderFlowLink,
  getConfig,
  OffersTableProps,
} from 'utils/offersTableConfigs';
import { useOffersData, useProductsAccess } from 'hooks';
import useTrackRenderDuration from 'hooks/useTrackRenderDuration';
import { createOfferTableEvents, trackProductsRenderedDuration } from 'eventsTracking';
import ProductsTabs from '../ProductsTabs';
import * as S from './styles';
import { DBSProduct } from 'types';
import messages from 'messages';

export const OffersTable: FC<OffersTableProps> = ({
  tabs,
  filters,
  user_filters_visibility,
  display_config,
  cta,
  isWrappedByLayoutContainer,
}) => {
  const t = useT();
  const intl = useIntl();
  const { isLoggedIn } = useAuthContext();
  const { customerData } = useHeaderDataContext();
  const router = useRouter();
  const locale = getLocale(router?.locale || DEFAULT_LOCALE)?.intlFormat;

  const [isLoading, setIsLoading] = useState(true);
  const { offersData } = useOffersData(locale, isLoggedIn);
  const { distributorId } = useProductsAccess();

  const offers = useMemo(() => parseDepositOfferData(offersData), [offersData]);
  const offersTableDefaultConfig = useMemo(
    () =>
      getConfig({
        locale,
        filters,
        user_filters_visibility,
        tabs,
        display_config,
        cta,
      }),
    [locale, filters, user_filters_visibility, tabs, display_config, cta],
  );

  const {
    offersTableConfig,
    BUTTON_LINK_CTA_DESKTOP,
    BUTTON_LINK_CTA_MOBILE,
    ENABLE_TABS,
    ACTIVATED_TABS,
    DEFAULT_TAB,
  } = offersTableDefaultConfig;

  const onInvestNowClick = async (offer: DBSProduct, isLoggedInUser) => {
    window.location.href = isLoggedInUser
      ? await buildOpeningOrderFlowLink(locale, offer)
      : await buildAsyncRegistrationLink(locale, BUTTON_LINK_CTA_DESKTOP, offer);
  };

  const onMobileInvestNowClick = async (offer: DBSProduct, isLoggedInUser) => {
    window.location.href = isLoggedInUser
      ? await buildOpeningOrderFlowLink(locale, offer)
      : await buildAsyncRegistrationLink(locale, BUTTON_LINK_CTA_MOBILE, offer);
  };

  const OFFER_TABLE_EVENTS = createOfferTableEvents({
    isLoggedIn,
    distributorId: distributorId || getDistributor(),
  });

  useTrackRenderDuration(trackProductsRenderedDuration(OFFER_TABLE_EVENTS), isLoading);
  useEffect(() => {
    if (!isEmpty(offersData)) {
      setIsLoading(false);
    }
  }, [offersData]);

  const renderOffBoardingNotification = () => (
    <Box mt={6}>
      <Alert mode="warning">
        <Typography variant="subtitle1">{t(messages.customerOffBoardingTitle)}</Typography>
        <Typography variant="body1">{t(messages.customerOffBoardingBody)}</Typography>
      </Alert>
    </Box>
  );

  return (
    <S.PageContainer>
      {isLoading ? (
        <CircularProgress data-testid="spinner" />
      ) : (
        <S.TableContainer>
          {isCustomerOffBoarding({ status: customerData?.status }) &&
            renderOffBoardingNotification()}
          <ProductsTabs
            loading={isLoading}
            offers={offers}
            onInvestNowClick={onInvestNowClick}
            onMobileInvestNowClick={onMobileInvestNowClick}
            intl={intl}
            locale={locale}
            config={offersTableConfig}
            ACTIVATED_TABS={ENABLE_TABS ? ACTIVATED_TABS : [DEFAULT_TAB]}
            DEFAULT_TAB={DEFAULT_TAB}
            isLoggedIn={isLoggedIn}
            isWrappedByLayoutContainer={isWrappedByLayoutContainer}
            OFFER_TABLE_EVENTS={OFFER_TABLE_EVENTS}
          />
        </S.TableContainer>
      )}
    </S.PageContainer>
  );
};
